import CheckCircleFillGreenSvg from "@/src/assets/icons/icon-checkmark-circle-green-fill.svg";
import CheckCircleRedSvg from "@/src/assets/icons/icon-checkmark-circle-red.svg";
import CheckCircleGrayFillSvg from "@/src/assets/icons/icon-checkmark-circle-gray-fill.svg";
import EyeOffSvg from "@/src/assets/icons/icon-eye-off.svg";
import EyeSvg from "@/src/assets/icons/icon-eye.svg";
import BasicButton from "@/src/components/atom/Button/BasicButton";
import Checkbox from "@/src/components/atom/Checkbox";
import Dialog from "@/src/components/atom/Dialog";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import IconInput from "@/src/components/atom/Input/IconInput";
import SearchSelect from "@/src/components/atom/Select/SearchSelect";
import Typo from "@/src/components/atom/Typo";
import { InputError, InputSuccess } from "@/src/components/molecule/FormItem";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import PUBLIC_PATH from "@/src/routes/public/path";
import { ENGLISH_NAME_ALLOWED } from "@/src/constants/regex";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import {
  EmailStatus,
  useCheckEmailDomainMutation,
  useCheckSendEmailCodeMutation,
  useSendTokenEmailMutation,
  useIndividualSignUpMutation,
  useSignInMutation,
} from "@/src/store/apis/auth";
import { useAppDispatch } from "@/src/store";
import { setUser } from "@/src/store/slice/auth";
import {
  useGetCommonCodeViaCodeNameQuery,
  useGetTermsQuery,
} from "@/src/store/apis/common";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { css, styled } from "styled-components";

type Step = "email" | "verification" | "memberInfo";

const SEPARATOR = "&";

const SignupV2Page = () => {
  const { t, i18n } = useTranslation(["signup", "common", "error"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Step management
  const [step, setStep] = useState<Step>("email");

  // Email step state
  const [email, setEmail] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorType, setErrorType] = useState<
    | "required"
    | "invalid"
    | "inUse"
    | "api"
    | "blockedEmail"
    | "signupInProgress"
    | ""
  >("");

  // Verification step state
  const verificationInputRef = useRef<HTMLInputElement>(null);
  const [authTokenId, setAuthTokenId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false); // 인증 성공 여부
  const [verificationError, setVerificationError] = useState<
    "required" | "invalid" | "expired" | "api" | ""
  >("");

  // Member info step state
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [contact, setContact] = useState("");
  const [contactPrefix, setContactPrefix] = useState("");
  const [passwordVisible, setPasswordVisible] = useState({
    password: false,
  });
  const [requiredTermsIdList, setRequiredTermsIdList] = useState<number[]>([]);
  const [optionalTermsHistoryIdList, setOptionalTermsHistoryIdList] = useState<
    number[]
  >([]);
  const [isMarketingEmailReceive, setIsMarketingEmailReceive] = useState(false);
  const [memberInfoErrors, setMemberInfoErrors] = useState<{
    name?: string;
    password?: string;
    passwordErrors?: string[]; // 개별 비밀번호 에러들
    terms?: string;
    api?: string; // API 에러 메시지
  }>({});
  const [viewingTerm, setViewingTerm] = useState<{
    titleEn: string;
    bodyEn: string;
    titleKo: string;
    bodyKo: string;
  } | null>(null);

  // API hooks
  const [sendTokenEmail] = useSendTokenEmailMutation();
  const [checkSendEmailCode] = useCheckSendEmailCodeMutation();
  const [checkEmailDomain] = useCheckEmailDomainMutation();
  const [individualSignUp] = useIndividualSignUpMutation();
  const [signIn] = useSignInMutation();
  const { data: termsResponse = [] } = useGetTermsQuery({
    service: "EXPORT_WEB",
  });

  // Country code query
  const { countryCodeOptionList } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "COUNTRY",
    },
    {
      selectFromResult: ({ currentData, isError }) => ({
        countryCodeOptionList: !isError
          ? (currentData ?? [])
              .filter((val) => val.value1) // value1이 있는 국가만 표시
              .reduce<{ label: string; value: string }[]>((acc, val) => {
                return [
                  ...acc,
                  {
                    label: `${val.value1} (${val.codeItemName})`,
                    value: [
                      val.codeItemName,
                      val.codeItemNameEn,
                      val.codeItemNameKo,
                      val.value1,
                    ].join(SEPARATOR),
                  },
                ];
              }, [])
          : [],
      }),
      refetchOnMountOrArgChange: true,
    },
  );

  // Timer effect for verification step
  useEffect(() => {
    if (step === "verification" && timeLeft > 0 && !isVerified) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [step, timeLeft, isVerified]);

  useEffect(() => {
    if (step === "verification") {
      verificationInputRef.current?.focus();
    }
  }, [step]);


  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleEmailNext = async () => {
    if (!email) {
      setShowError(true);
      setErrorType("required");
      return;
    }

    if (!validateEmail(email)) {
      setShowError(true);
      setErrorType("invalid");
      return;
    }

    try {
      const domainCheckResult = await checkEmailDomain({ email }).unwrap();

      if (domainCheckResult.emailStatus === EmailStatus.IN_USE) {
        setShowError(true);
        setErrorType("inUse");
        return;
      }

      if (domainCheckResult.emailStatus === EmailStatus.SIGNUP_IN_PROGRESS) {
        setShowError(true);
        setErrorType("signupInProgress");
        return;
      }

      try {
        const result = await sendTokenEmail({ contact: email }).unwrap();
        setAuthTokenId(result.authTokenId);
        setTimeLeft(180);
        setStep("verification");
        setShowError(false);
        setErrorType("");
      } catch (sendError) {
        const err = sendError as {
          status?: number;
          data?: { errorCode?: string; message?: string };
        };

        if (err.data?.errorCode === "AUTH_TOKEN_CONTACT_DOES_NOT_MATCH_CONTACT_TYPE") {
          setShowError(true);
          setErrorType("invalid");
        } else {
          setShowError(true);
          setErrorType("api");
        }
        console.error("Send verification code failed:", sendError);
        return;
      }
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };
      if (err.status === 400) {
        setShowError(true);
        setErrorType("blockedEmail");
      } else {
        setShowError(true);
        setErrorType("api");
      }
      console.error("Email domain check failed:", error);
      return;
    }
  };

  const handleResend = async () => {
    try {
      const result = await sendTokenEmail({ contact: email }).unwrap();
      setAuthTokenId(result.authTokenId);
      setTimeLeft(180);
      setVerificationError("");
    } catch (error) {
      setVerificationError("api");
      console.error("Resend verification code failed:", error);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setVerificationError("required");
      return;
    }

    if (timeLeft <= 0) {
      setVerificationError("expired");
      return;
    }

    setIsVerifying(true);
    try {
      await checkSendEmailCode({
        authTokenId,
        verifyString: verificationCode,
      }).unwrap();

      setVerificationError("");
      setIsVerified(true);
    } catch (error) {
      const err = error as { status?: number };
      if (err.status === 400 || err.status === 404) {
        setVerificationError("invalid");
      } else {
        setVerificationError("api");
      }
      console.error("Verification failed:", error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleNextFromVerification = () => {
    if (!isVerified) {
      return;
    }
    setStep("memberInfo");
  };

  const handleBackFromVerification = () => {
    setStep("email");
    setVerificationCode("");
    setVerificationError("");
    setIsVerified(false);
    setTimeLeft(180);
  };

  const handleBackFromMemberInfo = () => {
    setStep("verification");
    setMemberInfoErrors({});
  };

  const getPasswordValidation = (passwordValue: string = password) => {
    return {
      hasLength: passwordValue.length >= 8 && passwordValue.length <= 20,
      hasNumber: /\d/.test(passwordValue),
      hasLetter: /[a-zA-Z]/.test(passwordValue),
      hasSpecial: /[!@#$%^*+=-_?]/.test(passwordValue),
    };
  };

  const isPasswordValid = (passwordValue: string = password): boolean => {
    const validation = getPasswordValidation(passwordValue);
    return validation.hasLength && validation.hasNumber && validation.hasLetter && validation.hasSpecial;
  };

  const validateMemberInfo = (): boolean => {
    const errors: typeof memberInfoErrors = {};
    let isValid = true;

    if (!name.trim()) {
      errors.name = t("error:required");
      isValid = false;
    }

    if (!password) {
      errors.password = t("error:required");
      isValid = false;
    } else if (!isPasswordValid()) {
      errors.password = t("signup:v2.passwordRequirements");
      isValid = false;
    }

    // Check required terms
    const requiredTermIds = termsResponse
      .filter((term) => !term.isOptional)
      .map(({ currentTermsHistory }) => currentTermsHistory.id);

    const allRequiredChecked = requiredTermIds.every((id) =>
      requiredTermsIdList.includes(id),
    );

    if (!allRequiredChecked) {
      errors.terms = t("error:required");
      isValid = false;
    }

    setMemberInfoErrors(errors);
    return isValid;
  };

  const handleCreateAccount = async () => {
    if (!validateMemberInfo()) {
      return;
    }

    try {
      const parsedContactPrefix = contactPrefix
        ? contactPrefix.split(SEPARATOR)[3] || undefined
        : undefined;

      // Step 1: 계정 생성
      await individualSignUp({
        email,
        password,
        name,
        personalContactPrefix: parsedContactPrefix,
        personalContact: contact.trim() || undefined, // 빈 문자열이면 undefined로 전송
        requiredTermsIdList,
        optionalTermsHistoryIdList,
        isMarketingEmailReceive,
      }).unwrap();

      // Step 2: 자동 로그인
      const loginResult = await signIn({
        aId: email,
        password,
      }).unwrap();

      // Step 3: Redux에 사용자 정보 저장
      if (loginResult.statusCode === 201) {
        const { row } = loginResult;
        dispatch(setUser(row));

        if ("hasExporterUser" in row && !row.hasExporterUser) {
          const { accountDto, companyExists, exporter } = row;

          if (companyExists) {
            navigate(PUBLIC_PATH.SIGNUP_MEMBER, {
              state: {
                companyName: exporter?.companyName,
                email: accountDto.email,
                name: name,
              },
            });
          } else {
            navigate(PUBLIC_PATH.SIGNUP_WORKSPACE);
          }
        }
      }
    } catch (error) {
      const err = error as {
        status?: number;
        data?: { message?: string | string[] };
      };

      // 에러 메시지 추출
      let errorMessage = t("error:unexpectedError");
      if (err.data?.message) {
        if (Array.isArray(err.data.message)) {
          errorMessage = err.data.message.join(", ");
        } else {
          errorMessage = err.data.message;
        }
      }

      // memberInfo 스텝에서 에러 표시 (페이지 이동하지 않음)
      setMemberInfoErrors((prev) => ({ ...prev, api: errorMessage }));
      console.error("Account creation or login failed:", error);
    }
  };

  return (
    <Layout>
      <PublicPageHeader />

      {/* Main Section */}
      <MainSection>
        {step === "email" && (
          <>
            {/* Email Step */}
            <TitleArea>
              <PageTitle>
                <Typo typoType="d3" as="h1" color="gray1">
                  {t("signup:common.enterEmailAddress")}
                </Typo>
              </PageTitle>
              <Subtitle>
                <Typo typoType="b6r" color="gray4">
                  {t("signup:v2.emailSubtitle")}
                </Typo>
              </Subtitle>
            </TitleArea>

            <FormContainer>
              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("common:id")}
                  </Typo>
                  <Required>*</Required>
                </Label>
                <Input
                  type="email"
                  placeholder={t("signup:v2.enterEmailPlaceholder")}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (showError) {
                      setShowError(false);
                      setErrorType("");
                    }
                  }}
                  onClear={() => setEmail("")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEmailNext();
                    }
                  }}
                  data-invalid={showError}
                />
                {showError && errorType && (
                  <InputError
                    message={
                      <>
                        {errorType === "required" && t("error:required")}
                        {errorType === "invalid" &&
                          t("error:login.emailTypeMissMatching")}
                        {errorType === "inUse" && t("error:alreadyExistedId")}
                        {errorType === "signupInProgress" &&
                          t("error:alreadyExistedId")}
                        {errorType === "blockedEmail" &&
                          t("signup:v2.blockedEmail")}
                        {errorType === "api" && t("error:unexpectedError")}
                      </>
                    }
                  />
                )}
              </FormField>

              <BasicButton
                buttonGrade="primary"
                buttonColor="blue"
                buttonSize={40}
                onClick={handleEmailNext}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typo typoType="btn3m" color="white">
                  {t("common:next")}
                </Typo>
              </BasicButton>

              <SignInSection>
                <Typo typoType="b9m" color="gray1">
                  {t("signup:common.alreadyHaveAnAccount")}
                </Typo>
                <SignInLink onClick={() => navigate(PUBLIC_PATH.LOGIN)}>
                  <Typo typoType="b9m" color="systemBlue2">
                    {t("common:login")}
                  </Typo>
                </SignInLink>
              </SignInSection>
            </FormContainer>
          </>
        )}

        {step === "verification" && (
          <>
            <TitleArea>
              <PageTitle>
                <Typo typoType="d3" as="h1" color="gray1">
                  {t("signup:v2.verificationTitle")}
                </Typo>
              </PageTitle>
              <Subtitle>
                <Typo typoType="b6r" color="gray4">
                  {t("signup:v2.verificationSubtitle", { email })}
                </Typo>
              </Subtitle>
            </TitleArea>

            <FormContainer>
              <FormField $hasMessage={!!verificationError || isVerified}>
                <LabelRow>
                  <Label>
                    <Typo typoType="b7m" color="gray2">
                      {t("signup:v2.code")}
                    </Typo>
                    <Required>*</Required>
                  </Label>
                  <BasicButton
                    buttonGrade="tertiary"
                    buttonColor="black"
                    buttonSize={24}
                    onClick={handleResend}
                    disabled={isVerified}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typo typoType="btn4m" color={isVerified ? "gray8" : "gray2"}>
                      {t("signup:common.resend")}
                    </Typo>
                  </BasicButton>
                </LabelRow>
                <VerificationInputRow>
                  <Input
                    ref={verificationInputRef}
                    type="text"
                    placeholder={t("signup:v2.enterCodePlaceHolder")}
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                      if (verificationError) {
                        setVerificationError("");
                      }
                    }}
                    onClear={() => setVerificationCode("")}
                    data-invalid={!!verificationError}
                    style={{ paddingRight: "10px" }}
                    suffix={
                      <TimerText $active={timeLeft > 0 && !isVerified}>
                        {t("signup:v2.timerFormat", {
                          time: formatTime(timeLeft),
                        })}
                      </TimerText>
                    }
                  />
                  <BasicButton
                    buttonGrade="secondary"
                    buttonColor="blue"
                    buttonSize={40}
                    onClick={handleVerifyCode}
                    disabled={!verificationCode || isVerifying || timeLeft <= 0 || isVerified}
                    style={{
                      minWidth: "114px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typo
                      typoType="btn3m"
                      color={
                        verificationCode && timeLeft > 0 && !isVerified ? "indigo" : "gray8"
                      }
                    >
                      {t("signup:v2.verifyCode")}
                    </Typo>
                  </BasicButton>
                </VerificationInputRow>
                {verificationError && (
                  <InputError
                    message={
                      <>
                        {verificationError === "required" &&
                          t("error:required")}
                        {verificationError === "invalid" &&
                          t("error:sendCodeValidationError")}
                        {verificationError === "expired" &&
                          t("signup:v2.errorCodeExpired")}
                        {verificationError === "api" &&
                          t("error:unexpectedError")}
                      </>
                    }
                  />
                )}
                {isVerified && (
                  <InputSuccess message={t("signup:v2.verificationSuccess")} />
                )}
              </FormField>
            </FormContainer>

            <ButtonGroup>
              <BasicButton
                buttonGrade="tertiary"
                buttonColor="black"
                buttonSize={40}
                onClick={handleBackFromVerification}
                style={{
                  width: "160px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typo typoType="btn3m" color="gray2">
                  {t("common:back")}
                </Typo>
              </BasicButton>
              <BasicButton
                buttonGrade="primary"
                buttonColor="blue"
                buttonSize={40}
                onClick={handleNextFromVerification}
                disabled={!isVerified}
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typo typoType="btn3m" color="white">
                  {t("common:next")}
                </Typo>
              </BasicButton>
            </ButtonGroup>
          </>
        )}

        {step === "memberInfo" && (
          <>
            {/* Member Info Step */}
            <TitleArea>
              <PageTitle>
                <Typo typoType="d3" as="h1" color="gray1">
                  {t("signup:v2.memberInfoTitle")}
                </Typo>
              </PageTitle>
              <Subtitle>
                <Typo typoType="b6r" color="gray4">
                  {t("signup:v2.memberInfoSubtitle")}
                </Typo>
              </Subtitle>
            </TitleArea>

            <FormContainer>
              {/* Name Field */}
              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.name")}
                  </Typo>
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  placeholder={t("signup:v2.namePlaceholder")}
                  value={name}
                  onChange={(e) => {
                    const original = e.target.value;
                    const filtered = original.replace(ENGLISH_NAME_ALLOWED, "");
                    setName(filtered);

                    if (original !== filtered) {
                      setMemberInfoErrors((prev) => ({
                        ...prev,
                        name: t("error:nameCharacterRule"),
                        api: undefined,
                      }));
                    } else if (memberInfoErrors.name || memberInfoErrors.api) {
                      setMemberInfoErrors((prev) => ({
                        ...prev,
                        name: undefined,
                        api: undefined,
                      }));
                    }
                  }}
                  onClear={() => setName("")}
                  data-invalid={!!memberInfoErrors.name}
                  autoComplete="off"
                />
                {memberInfoErrors.name && (
                  <InputError message={memberInfoErrors.name} />
                )}
              </FormField>

              {/* Password Field */}
              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.password")}
                  </Typo>
                  <Required>*</Required>
                </Label>
                <IconInput
                  type={passwordVisible.password ? "text" : "password"}
                  placeholder={t("signup:v2.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (
                      memberInfoErrors.passwordErrors ||
                      memberInfoErrors.api
                    ) {
                      setMemberInfoErrors((prev) => ({
                        ...prev,
                        passwordErrors: undefined,
                        api: undefined,
                      }));
                    }
                  }}
                  onClear={() => setPassword("")}
                  data-invalid={!!memberInfoErrors.password}
                  autoComplete="new-password"
                  icon={
                    <Icon
                      iconSrc={passwordVisible.password ? EyeSvg : EyeOffSvg}
                      iconSize={20}
                      onClick={() =>
                        setPasswordVisible((prev) => ({
                          ...prev,
                          password: !prev.password,
                        }))
                      }
                    />
                  }
                />
                <PasswordRequirementsContainer>
                    <Typo typoType="b9r" color="gray6">
                      {t("signup:v2.passwordRequirements")}
                    </Typo>
                    <PasswordRequirementsList>
                      <PasswordRequirementItem>
                        <Icon
                          iconSrc={
                            password.length === 0
                              ? CheckCircleGrayFillSvg
                              : getPasswordValidation().hasNumber
                                ? CheckCircleFillGreenSvg
                                : CheckCircleRedSvg
                          }
                          iconSize={16}
                        />
                        <Typo
                          typoType="b9r"
                          color={
                            password.length === 0
                              ? "gray6"
                              : getPasswordValidation().hasNumber
                                ? "green1"
                                : "red2"
                          }
                        >
                          {t("signup:v2.passwordReq1")}
                        </Typo>
                      </PasswordRequirementItem>
                      <PasswordRequirementItem>
                        <Icon
                          iconSrc={
                            password.length === 0
                              ? CheckCircleGrayFillSvg
                              : getPasswordValidation().hasLetter
                                ? CheckCircleFillGreenSvg
                                : CheckCircleRedSvg
                          }
                          iconSize={16}
                        />
                        <Typo
                          typoType="b9r"
                          color={
                            password.length === 0
                              ? "gray6"
                              : getPasswordValidation().hasLetter
                                ? "green1"
                                : "red2"
                          }
                        >
                          {t("signup:v2.passwordReq2")}
                        </Typo>
                      </PasswordRequirementItem>
                      <PasswordRequirementItem>
                        <Icon
                          iconSrc={
                            password.length === 0
                              ? CheckCircleGrayFillSvg
                              : getPasswordValidation().hasSpecial
                                ? CheckCircleFillGreenSvg
                                : CheckCircleRedSvg
                          }
                          iconSize={16}
                        />
                        <Typo
                          typoType="b9r"
                          color={
                            password.length === 0
                              ? "gray6"
                              : getPasswordValidation().hasSpecial
                                ? "green1"
                                : "red2"
                          }
                        >
                          {t("signup:v2.passwordReq3")}
                        </Typo>
                      </PasswordRequirementItem>
                    </PasswordRequirementsList>
                  </PasswordRequirementsContainer>
              </FormField>

              {/* Contact Field */}
              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("signup:v2.contactPersonal")}
                  </Typo>
                </Label>
                <ContactRow>
                  <SearchSelect
                    value={contactPrefix}
                    onChange={(value) => setContactPrefix(value as string)}
                    options={countryCodeOptionList}
                    style={{ width: "140px" }}
                  />
                  <Input
                    type="text"
                    placeholder={t("signup:v2.contactPlaceholder")}
                    value={contact}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setContact(value);
                      if (memberInfoErrors.api) {
                        setMemberInfoErrors((prev) => ({
                          ...prev,
                          api: undefined,
                        }));
                      }
                    }}
                    onClear={() => setContact("")}
                    style={{ flex: 1 }}
                  />
                </ContactRow>
              </FormField>

              {/* Terms Section */}
              <TermsSection>
                <TermsField>
                  <TermsRow>
                    <Checkbox
                      checked={
                        requiredTermsIdList.length ===
                          termsResponse.filter((t) => !t.isOptional).length &&
                        optionalTermsHistoryIdList.length ===
                          termsResponse.filter((t) => t.isOptional).length &&
                        isMarketingEmailReceive
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRequiredTermsIdList(
                            termsResponse
                              .filter((t) => !t.isOptional)
                              .map((t) => t.currentTermsHistory.id),
                          );
                          setOptionalTermsHistoryIdList(
                            termsResponse
                              .filter((t) => t.isOptional)
                              .map((t) => t.currentTermsHistory.id),
                          );
                          setIsMarketingEmailReceive(true);
                        } else {
                          setRequiredTermsIdList([]);
                          setOptionalTermsHistoryIdList([]);
                          setIsMarketingEmailReceive(false);
                        }
                      }}
                    />
                    <Typo typoType="b7r" color="gray1">
                      {t("signup:v2.agreeAll")}
                    </Typo>
                  </TermsRow>
                </TermsField>

                <TermsDivider />

                {termsResponse.map((term) => (
                  <TermsField key={term.currentTermsHistory.id}>
                    <TermsRow>
                      <Checkbox
                        checked={
                          term.isOptional
                            ? optionalTermsHistoryIdList.includes(
                                term.currentTermsHistory.id,
                              )
                            : requiredTermsIdList.includes(
                                term.currentTermsHistory.id,
                              )
                        }
                        onChange={(e) => {
                          if (term.isOptional) {
                            setOptionalTermsHistoryIdList((prev) =>
                              e.target.checked
                                ? [...prev, term.currentTermsHistory.id]
                                : prev.filter(
                                    (id) => id !== term.currentTermsHistory.id,
                                  ),
                            );
                          } else {
                            setRequiredTermsIdList((prev) =>
                              e.target.checked
                                ? [...prev, term.currentTermsHistory.id]
                                : prev.filter(
                                    (id) => id !== term.currentTermsHistory.id,
                                  ),
                            );
                          }
                        }}
                      />
                      <Typo typoType="b7r" color="gray1">
                        {(term.titleEn === "Terms of Service" || term.titleEn === "Terms of Use") ? t("signup:v2.terms") :
                         term.titleEn === "Privacy Policy" ? t("signup:v2.privacy") :
                         term.titleEn === "Terms of Use for Location Information" ? t("signup:v2.locationTerms") :
                         term.titleKo}{" "}
                        {!term.isOptional && (
                          <RequiredText>({t("common:requiredItem")})</RequiredText>
                        )}
                      </Typo>
                      <TermsViewLink
                        onClick={() => {
                          setViewingTerm({
                            bodyEn: term.currentTermsHistory.bodyEn,
                            bodyKo: term.currentTermsHistory.bodyKo,
                            titleEn: term.titleEn,
                            titleKo: term.titleKo,
                          });
                        }}
                      >
                        <Typo typoType="b9r" color="gray6">
                          {t("common:view")}
                        </Typo>
                      </TermsViewLink>
                    </TermsRow>
                  </TermsField>
                ))}

                <TermsField>
                  <TermsRow>
                    <Checkbox
                      checked={isMarketingEmailReceive}
                      onChange={(e) =>
                        setIsMarketingEmailReceive(e.target.checked)
                      }
                    />
                    <Typo typoType="b7r" color="gray1">
                      {t("signup:v2.marketingEmail")}{" "}
                      <OptionalText>({t("common:optional")})</OptionalText>
                    </Typo>
                  </TermsRow>
                </TermsField>

                {memberInfoErrors.terms && (
                  <InputError message={memberInfoErrors.terms} />
                )}
              </TermsSection>

              {/* API Error Message */}
              {memberInfoErrors.api && (
                <InputError message={memberInfoErrors.api} />
              )}
            </FormContainer>

            <ButtonGroup>
              <BasicButton
                buttonGrade="tertiary"
                buttonColor="black"
                buttonSize={40}
                onClick={handleBackFromMemberInfo}
                style={{
                  width: "160px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typo typoType="btn3m" color="gray2">
                  {t("common:back")}
                </Typo>
              </BasicButton>
              <BasicButton
                buttonGrade="primary"
                buttonColor="blue"
                buttonSize={40}
                onClick={handleCreateAccount}
                disabled={
                  !name.trim() ||
                  !isPasswordValid() ||
                  requiredTermsIdList.length !==
                    termsResponse.filter((t) => !t.isOptional).length
                }
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typo typoType="btn3m" color="white">
                  {t("signup:v2.createAccount")}
                </Typo>
              </BasicButton>
            </ButtonGroup>
          </>
        )}
      </MainSection>

      <PublicPageFooter />

      {viewingTerm && (
        <Dialog
          open
          onOpenChange={(openState) => {
            if (!openState) {
              setViewingTerm(null);
            }
          }}
          width={850}
          title={
            i18n.language === "ko" ? viewingTerm.titleKo : viewingTerm.titleEn
          }
        >
          <TermContentContainer
            className="ck ck-content"
            dangerouslySetInnerHTML={{
              __html:
                i18n.language === "ko"
                  ? viewingTerm?.bodyKo || "-"
                  : viewingTerm?.bodyEn || "-",
            }}
          />
        </Dialog>
      )}
    </Layout>
  );
};

export default SignupV2Page;

// Styled Components
const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: ${colorSet.white};
`;

const MainSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 40px 40px;
  flex: 1;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  gap: 40px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 40px;
`;

const PageTitle = styled.div`
  text-align: center;
  width: 100%;
  padding: 24px 0;

  h1 {
    margin: 0;
  }
`;

const Subtitle = styled.div`
  text-align: center;
  width: 100%;
  padding: 8px 0;
  white-space: pre-wrap;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 520px;
`;

const FormField = styled.div<{ $hasMessage?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  ${({ $hasMessage }) =>
    $hasMessage &&
    css`
      margin-bottom: -8px; /* 40px - 32px = 8px 감소 */
    `}
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;


const SignInSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 16px 0;
`;

const SignInLink = styled.span`
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

// Verification step styled components
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

const VerificationInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const TimerText = styled.div<{ $active?: boolean }>`
  ${typo.b9m};
  color: ${({ $active }) => ($active ? colorSet.red2 : colorSet.gray7)};
  min-width: 65px;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 520px;
`;

// Member info step styled components
const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const PasswordRequirementsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PasswordRequirementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PasswordRequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TermsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const TermsDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${colorSet.gray9};
`;

const TermsField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const TermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  input[type="checkbox"] {
    margin-top: 2px;
  }
`;

const TermsViewLink = styled.span`
  margin-left: auto;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const OptionalText = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: ${colorSet.gray6};
  font-weight: 400;
  font-family: "Pretendard", -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif;
`;

const Required = styled.span`
  color: ${colorSet.red2};
`;

const RequiredText = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: ${colorSet.blue4};
  font-weight: 400;
  font-family: "Pretendard", -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif;
`;

const TermContentContainer = styled.div`
  ul {
    list-style: none;
    padding-left: 1em;
  }
`;
